import type { WebinarRoomPollViewDto } from '@/api/webinarRoom'
import { formatRecordingClock } from '@/webinarRoom/time'
import { clusterPolls, formatPollTime, timelineTicks, type PollTimeline } from './roomPolls'

interface RoomPollTimelineProps {
    timeline: PollTimeline
    /** Sorted by time. */
    polls: readonly WebinarRoomPollViewDto[]
    /** Where the poll being typed would pop up, or null. */
    ghostSeconds: number | null
    /** Rows the pointer is over, so their marker lights up too. */
    highlightIds: readonly string[]
    onHover: (ids: string[] | null) => void
    /** A marker was pressed: bring its rows into view. */
    onMarker: (ids: string[]) => void
}

/**
 * Where each poll pops up, along the recording once it is ready and the session before. Polls under a minute
 * apart share a marker in the warning style; one past the limit is drawn as an error. A recording that runs past
 * the session shows the rest hatched.
 */
export function RoomPollTimeline({ timeline, polls, ghostSeconds, highlightIds, onHover, onMarker }: RoomPollTimelineProps) {
    const { lengthSeconds, limitSeconds, sessionEndSeconds } = timeline
    const pct = (seconds: number) => `${Math.min(100, Math.max(0, (seconds / lengthSeconds) * 100))}%`
    const ticks = timelineTicks(lengthSeconds)

    return (
        <div className="mt-6 pt-4" role="group" aria-label={`Where each poll pops up in the ${timeline.basis}`}>
            <div className="relative h-5">
                <span className="absolute inset-x-0 top-2 h-1 rounded-full bg-slate-200 dark:bg-slate-700" />
                {sessionEndSeconds !== null && (
                    <>
                        <span
                            className="absolute top-1.5 right-0 h-2 rounded-r-full opacity-50"
                            style={{ left: pct(sessionEndSeconds), background: 'repeating-linear-gradient(135deg, rgb(217 119 6) 0 2px, transparent 2px 6px)' }}
                        />
                        <span className="absolute -top-0.5 w-0.5 h-6 -ml-px rounded bg-amber-600" style={{ left: pct(sessionEndSeconds) }}>
                            <span className="absolute bottom-[calc(100%+4px)] -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold text-amber-700 dark:text-amber-400">Session ends</span>
                        </span>
                    </>
                )}
                {clusterPolls(polls).map(cluster => {
                    const ids = cluster.map(poll => poll.Id)
                    const times = cluster.map(poll => formatPollTime(poll.OffsetSeconds))
                    const shared = cluster.length > 1
                    const out = cluster.some(poll => poll.OffsetSeconds >= limitSeconds)
                    const lit = ids.some(id => highlightIds.includes(id))
                    const tone = out
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/40'
                        : shared ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/40' : 'border-slate-500 dark:border-slate-400 bg-white dark:bg-slate-800'
                    const size = shared ? 'w-6 h-[18px] -ml-3 top-px' : 'w-3.5 h-3.5 -ml-[7px] top-[3px]'
                    return (
                        <button
                            key={ids.join(' ')}
                            type="button"
                            aria-label={shared ? `${cluster.length} polls, at ${times.join(' and ')}` : `Poll at ${times[0]}: ${cluster[0].Question}`}
                            className={`group absolute grid place-items-center rounded-full border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 hover:scale-110 ${size} ${tone} ${lit ? 'scale-110 border-slate-900! dark:border-white!' : ''}`}
                            style={{ left: pct(cluster[0].OffsetSeconds) }}
                            onMouseEnter={() => onHover(ids)}
                            onMouseLeave={() => onHover(null)}
                            onClick={() => onMarker(ids)}
                        >
                            {shared && <span aria-hidden="true" className={`text-[10px] font-bold leading-none tabular-nums ${out ? 'text-red-700 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'}`}>{cluster.length}</span>}
                            <span className={`pointer-events-none absolute bottom-[calc(100%+6px)] left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1.5 text-[11px] font-semibold leading-4 tabular-nums bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 transition-opacity ${lit ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'}`}>
                                {times.join(', ')}
                            </span>
                        </button>
                    )
                })}
                {ghostSeconds !== null && (
                    <span data-testid="poll-ghost-marker" className="pointer-events-none absolute top-[3px] w-3.5 h-3.5 -ml-[7px] rounded-full border-2 border-dashed border-primary-400 bg-primary-50 dark:bg-primary-900/30" style={{ left: pct(ghostSeconds) }}>
                        <span className="absolute bottom-[calc(100%+6px)] left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1.5 text-[11px] font-semibold leading-4 tabular-nums bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">{formatPollTime(ghostSeconds)}</span>
                    </span>
                )}
            </div>
            <div className="relative h-4 mt-1 text-[11px] leading-4 tabular-nums text-slate-500 dark:text-slate-400" aria-hidden="true">
                {ticks.map((tick, index) => (
                    <span
                        key={tick}
                        className={`absolute whitespace-nowrap ${index === 0 ? '' : index === ticks.length - 1 ? '-translate-x-full' : '-translate-x-1/2'}`}
                        style={{ left: pct(tick) }}
                    >
                        {index === ticks.length - 1 ? formatRecordingClock(tick) : formatPollTime(tick)}
                    </span>
                ))}
            </div>
        </div>
    )
}
